import Logo from '@assets/svg/logo-beebryte.svg'
import { STORAGE_KEY } from '@utils/constants/storage'
import { Dialog } from 'primereact/dialog'
import { useLocalStorage } from 'primereact/hooks'
import { TieredMenu } from 'primereact/tieredmenu'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useMatch } from 'react-router'

/**
 * Sidebar navigation item, recursively rendering its own children when expanded.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.item - Menu item definition (url, label, icon, items, namespace, visible, disabled).
 * @param {string} props.currentUrl - Current route path, used to detect the active item.
 * @param {Function} props.closeBurger - Callback to close the mobile burger menu on navigation.
 * @returns {JSX.Element}
 */
const Item = ({ item, currentUrl, closeBurger }) => {
  const [showChildren, setShowChildren] = useState(false)
  const pathMatch = useMatch(item.url)
  const isCurrent = useMemo(
    () => (!item.items && currentUrl.includes(item.namespace)) || !!pathMatch,
    [item, currentUrl, pathMatch]
  )
  const itemClassName = useMemo(() => {
    return ['sidebar-item', isCurrent ? 'open-item' : '', item.disabled ? 'disabled' : ''].join(' ')
  }, [isCurrent, item.disabled])
  const linkClassName = useMemo(() => {
    return ['sidebar-item-link', isCurrent ? 'current-page' : ''].join(' ')
  }, [isCurrent])

  /**
   * Determines whether an item's children should be shown, based on whether the current URL matches
   * any child item's namespace/url.
   *
   * @param {Object} object - Menu item whose children are being checked.
   * @param {string} url - Current route path to match against child items.
   * @returns {boolean}
   */
  const haveToShowChildren = (object, url) => {
    return !!(
      object.items &&
      object.items.filter(item => {
        return url.includes(item.namespace ?? item.url)
      }).length
    )
  }

  useEffect(() => {
    setShowChildren(isCurrent || haveToShowChildren(item, currentUrl))
  }, [isCurrent, item, currentUrl])

  return (
    <div className={itemClassName}>
      <Link
        to={item.url}
        className={linkClassName}
        onClick={closeBurger}
      >
        <span className="sidebar-item-icon">
          <i className={item.icon} />
        </span>
        {item.label}

        {item.items && !!item.items.length && (
          <i
            onClick={e => {
              e.stopPropagation()
              e.preventDefault()
              setShowChildren(!showChildren)
            }}
            className={`icon-dropdown fa ${showChildren ? 'fa fa-minus' : 'fa-plus'}`}
          />
        )}
      </Link>

      {showChildren &&
        item.items &&
        !!item.items.length &&
        item.items
          .filter(item => item.visible)
          .map((children, index) => {
            return (
              <Item
                item={children}
                key={index}
                currentUrl={currentUrl}
                closeBurger={closeBurger}
              />
            )
          })}
    </div>
  )
}

/**
 * Sidebar component.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.menu - Menu item definitions to render (filtered by `visible`).
 * @param {string} props.currentUrl - Current route path, forwarded to each `Item`.
 * @param {string} [props.mainTitle] - Optional main title displayed in the header.
 * @param {string} [props.siteName] - Optional site name displayed in the header.
 * @param {string} [props.logo] - Optional raw SVG/HTML logo markup.
 * @param {Object} [props.apiInfos] - API info object displayed in the about dialog.
 * @param {boolean} [props.loadingApiInfos] - Whether API info is still loading.
 * @param {Object} [props.coreInfos] - Core app version info displayed in the about dialog.
 * @param {string} props.logoUrl - URL the logo links to.
 * @param {string} [props.siteUrl] - URL the site name links to.
 * @param {Object} props.rest - Remaining props spread onto the root `<aside>` element's class name.
 * @returns {JSX.Element}
 */
export default function SideBar({
  menu,
  currentUrl,
  mainTitle,
  siteName,
  logo,
  apiInfos,
  loadingApiInfos,
  coreInfos,
  logoUrl,
  siteUrl,
  ...rest
}) {
  const themeSlug = STORAGE_KEY.THEME
  const [theme, setTheme] = useLocalStorage(localStorage.getItem(themeSlug) ?? 'system', themeSlug)
  const [burger, setBurger] = useState(false)
  const sidebar = useRef()
  const themeSelector = useRef(null)
  const [infosVisible, setInfosVisible] = useState(false)
  const { t } = useTranslation()

  const availableTheme = [
    {
      label: t('common.theme_system'),
      icon: 'fa-solid fa-laptop',
      disabled: theme === 'system',
      command: () => setTheme('system')
    },
    {
      label: t('common.theme_light'),
      icon: 'fa-solid fa-sun',
      disabled: theme === 'light',
      command: () => setTheme('light')
    },
    {
      label: t('common.theme_dark'),
      icon: 'fa-solid fa-moon',
      disabled: theme === 'dark',
      command: () => setTheme('dark')
    }
  ]

  /**
   * Toggles the mobile burger menu's visibility and stops the click from propagating to the mask.
   *
   * @param {Event} e - Click event.
   * @returns {void}
   */
  const toggleBurger = e => {
    e.stopPropagation()
    setBurger(!burger)
  }

  return (
    <aside className={['sidebar', burger ? 'visible' : '', rest.className].join(' ')}>
      <div
        className="sidebar-mask"
        onClick={toggleBurger}
      />
      <div
        className="sidebar-content"
        ref={sidebar}
      >
        <div className="sidebar-header">
          <div className="organization-logo">
            <Link to={logoUrl}>
              {logo ? (
                <div
                  className="sidebar-logo"
                  dangerouslySetInnerHTML={{ __html: logo }}
                />
              ) : (
                <img
                  className="sidebar-logo"
                  alt={t('common.logo')}
                  src={Logo}
                />
              )}
            </Link>
          </div>

          <button
            className={`sidebar-toggle ${burger ? 'menu-display' : ''}`}
            onClick={toggleBurger}
          >
            <span />
          </button>
        </div>

        <div className="sidebar-header-secondary">
          {mainTitle && <div className="main-title">{mainTitle}</div>}
          {siteName && siteUrl && (
            <Link to={siteUrl}>
              <div className="site-name">{siteName}</div>
            </Link>
          )}
        </div>

        {menu
          .filter(item => item.visible)
          .map((item, index) => {
            return (
              <Item
                item={item}
                key={index}
                currentUrl={currentUrl}
                closeBurger={() => setBurger(false)}
              />
            )
          })}

        <div className="sidebar-theme-selector">
          <input
            type="hidden"
            name="theme"
            readOnly
            value={theme}
          />

          <button
            className="app-version"
            onClick={() => setInfosVisible(true)}
          >
            <i className="fa-solid fa-circle-question" />
          </button>

          <button onClick={e => themeSelector.current.toggle(e)}>
            <i className="fa-solid fa-wand-magic-sparkles" />
          </button>

          <TieredMenu
            className="sidebar-theme-menu"
            model={availableTheme}
            ref={themeSelector}
            popup
          />
        </div>

        <Dialog
          className="sidebar-app-infos"
          header={t('common.about_hivolution')}
          visible={infosVisible}
          onHide={() => setInfosVisible(false)}
          dismissableMask
          draggable={false}
        >
          <div className="sidebar-app-infos-content">
            {coreInfos && (
              <>
                {coreInfos.version && (
                  <p>
                    <span>{t('common.hivolution_frontend')}</span>{' '}
                    <strong>{coreInfos.version}</strong>
                  </p>
                )}

                {coreInfos.utilsVersion && (
                  <p>
                    <span>{t('common.hivolution_frontend_utils')}</span>{' '}
                    <strong>{coreInfos.utilsVersion}</strong>
                  </p>
                )}
              </>
            )}

            {loadingApiInfos ? (
              <p>
                <span>{t('common.loading_api_informations')}</span>
                <i className="fa-solid fa-spinner fa-spin" />
              </p>
            ) : (
              apiInfos && (
                <p>
                  <span>{apiInfos.info.title}</span> <strong>{apiInfos.info.version}</strong>
                </p>
              )
            )}
          </div>
        </Dialog>
      </div>
    </aside>
  )
}
